/**
 * Date-window defaults for headline search requests.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THIS IS A CLIENT-SIDE WORKAROUND FOR TWO SERVER-SIDE BEHAVIOURS.
 * Both want fixing in the headlines service; delete this module when they are.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * (1) POST /headlines/search/full_text applies an undocumented lower bound of
 * roughly 30 days whenever the request carries no `start_date`. It is not
 * mentioned in the endpoint's OpenAPI description, and nothing in the UI tells
 * the user their search was bounded — so a watchlist for a ticker last
 * mentioned five weeks ago simply comes back empty, which reads as "no such
 * ticker" rather than "outside the window". Sending an explicit `start_date`
 * opts out. That is what this module does.
 *
 * (2) `end_date` IS NOT APPLIED AT ALL — not merely when `start_date` is
 * missing. Nothing this module (or any client) can send fixes that; the upper
 * bound of a user's date range is silently discarded server-side. The "To"
 * field in the headline search form is therefore inert until the backend is
 * fixed. We still forward `end_date` so it starts working the moment it is.
 *
 * Verified against the live service on 2026-09-14 (today − 30d ≈ 2026-08-15).
 * "muv2" occurs on 2026-08-07 and nowhere else in the corpus:
 *
 *   {"search_term":"muv2"}                                        ->  0 rows
 *   {"search_term":"muv2","date_range":{}}                        ->  0 rows
 *   {"search_term":"muv2","date_range":{"end_date":"2026-09-14"}} ->  0 rows
 *   {"search_term":"muv2","date_range":{"start_date":"2020-01-01"}}
 *                                                -> 4 rows, all 2026-08-07
 *   ...,"start_date":"2026-01-01","end_date":"2026-08-06"}        <- before the
 *                                                hits, yet still 4 rows: (2)
 *
 * Paginating an empty search_term back through time dead-ends at
 * 2026-08-16T21:09:34Z with has_more:false, and clears it immediately once an
 * explicit start_date is supplied — the floor, not an absence of data.
 *
 * Widening the window is close to free. Median of 3 against the live service,
 * limit 30, full-text path: ~195ms with no date_range, ~197ms at 30 days,
 * ~294ms at 120 days, ~302ms at 2 years, ~300ms unbounded. Cost tracks rows
 * returned rather than span, because the GIN index on `vector_words` drives the
 * plan either way. 120 days is therefore a conservative pick with headroom —
 * an arbitrary compromise to make search feel unbounded day to day, NOT a
 * product decision about how far back search should reach. That decision
 * belongs server-side, where the floor should simply be lifted.
 */

/**
 * How far back to look when the caller supplied no explicit start date.
 *
 * Arbitrary — see the module comment. Raising it is cheap; the real fix is the
 * backend dropping its implicit floor so the client need not send a bound at
 * all.
 */
export const DEFAULT_SEARCH_WINDOW_DAYS = 120;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface SearchDateRange {
  startDate?: string;
  endDate?: string;
}

/** The `date_range` payload the search endpoint expects. */
export interface SearchDateRangePayload {
  start_date?: string;
  end_date?: string;
}

/**
 * Build the `date_range` payload, filling in a start date when the caller has
 * not chosen one.
 *
 * When the user supplied an end date but no start date, the fallback window is
 * anchored to THEIR end date rather than to now, giving them the N days leading
 * up to the date they asked about. Anchoring to now instead would search the
 * last N days and ignore their input completely — and, because the service
 * drops `end_date` (see (2) above), nothing downstream would pull the results
 * back to the period they wanted.
 *
 * Note this only gets the LOWER bound pointing at the right period. Until the
 * backend honours `end_date`, an end-date-only search still returns everything
 * from `end - N days` through to today. Partial relief, not a fix.
 *
 * The end-anchored window also cannot invert, which keeps us inside the range
 * contract the service's own schema declares (start_date <= end_date) even
 * though the deployed build does not currently enforce it.
 *
 * @param now Injectable clock, for tests.
 */
export const resolveSearchDateRange = (
  range?: SearchDateRange,
  now: number = Date.now()
): SearchDateRangePayload => {
  const endDate = range?.endDate;

  // Caller gave a start date: pass both through untouched. Nothing to work
  // around — an explicit start_date is what lifts the server-side floor.
  if (range?.startDate) {
    return { start_date: range.startDate, end_date: endDate };
  }

  const parsedEnd = endDate ? Date.parse(endDate) : Number.NaN;
  const anchor = Number.isNaN(parsedEnd) ? now : parsedEnd;

  return {
    start_date: new Date(
      anchor - DEFAULT_SEARCH_WINDOW_DAYS * DAY_MS
    ).toISOString(),
    end_date: endDate,
  };
};
